import React, { useEffect, useState } from "react";
import AlertDialog from "../components/AlertDialog";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Typography, TextField, Button, Grid, Card } from "@mui/material";
import { api } from "common";
import { useTranslation } from "react-i18next";
import CircularLoading from "components/CircularLoading";
import { MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { colors } from "components/Theme/WebTheme";

const EMPTY_CAMPAIGN = {
  name: "",
  title: "",
  body: "",
  usertype: "All",
  devicetype: "All",
  status: "draft",
};

const EditCampaign = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const settings = useSelector((state) => state.settingsdata.settings);
  const campaigndata = useSelector((state) => state.campaigndata);
  const { fetchCampaigns, editCampaign } = api;

  const [data, setData] = useState(EMPTY_CAMPAIGN);
  const [loadedId, setLoadedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  useEffect(() => {
    if (!campaigndata.campaigns) {
      dispatch(fetchCampaigns());
    }
  }, [dispatch, fetchCampaigns, campaigndata.campaigns]);

  // fetchCampaigns keeps a live listener, so campaigndata.campaigns gets a new reference
  // whenever any campaign changes — including stats written by another admin's send.
  // Load the form once, or those updates would overwrite what is being typed.
  useEffect(() => {
    if (loadedId === id || !id || !campaigndata.campaigns) return;
    const existing = campaigndata.campaigns.find((c) => c.id === id);
    if (existing) {
      setData({ ...EMPTY_CAMPAIGN, ...existing });
      setLoadedId(id);
    }
  }, [id, loadedId, campaigndata.campaigns]);

  const handleInputChange = (e) => {
    setData({ ...data, [e.target.id]: e.target.value });
  };

  const handleSubmit = () => {
    if (!data.name || !data.title || !data.body) {
      setCommonAlert({ open: true, msg: t("no_details_error") });
      return;
    }
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }

    setLoading(true);
    // Saving never sends. Delivery is an explicit action from the campaign list, so a
    // typo in an edit can't blast the whole user base.
    if (id) {
      dispatch(editCampaign({ ...data, id }, "Update"));
    } else {
      dispatch(editCampaign({ ...data, createdAt: new Date().getTime() }, "Add"));
    }
    setLoading(false);
    navigate("/campaigns");
  };

  if (loading) return <CircularLoading />;

  return (
    <div>
      <Card
        style={{
          borderRadius: "19px",
          backgroundColor: colors.WHITE,
          minHeight: 100,
          marginTop: 20,
          padding: 20,
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
        }}
      >
        <Typography
          variant="h5"
          style={{
            margin: "10px 0",
            textAlign: isRTL === "rtl" ? "right" : "left",
            fontWeight: "bold",
            color: MAIN_COLOR,
            fontFamily: FONT_FAMILY,
          }}
        >
          {id ? t("edit_campaign") : t("add_campaign")}
        </Typography>
        <Button variant="text" onClick={() => navigate("/campaigns")}>
          <Typography
            style={{
              marginBottom: 10,
              fontWeight: "bold",
              color: MAIN_COLOR,
              fontFamily: FONT_FAMILY,
            }}
          >
            {`<<- ${t("go_back")}`}
          </Typography>
        </Button>

        <Grid container spacing={2} sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              label={t("campaign_name")}
              id="name"
              value={data.name}
              fullWidth
              onChange={handleInputChange}
              helperText={t("campaign_name_helper")}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              label={t("title")}
              id="title"
              value={data.title}
              fullWidth
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              label={t("body")}
              id="body"
              value={data.body}
              fullWidth
              multiline
              rows={3}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <FormControl fullWidth style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
              <InputLabel id="campaign_user_type" sx={{ "&.Mui-focused": { color: MAIN_COLOR } }}>
                {t("user_type")}
              </InputLabel>
              <Select
                labelId="campaign_user_type"
                id="campaign_user_type"
                value={data.usertype}
                label={t("user_type")}
                onChange={(e) => setData({ ...data, usertype: e.target.value })}
              >
                <MenuItem value="All">{t("all")}</MenuItem>
                <MenuItem value="customer">{t("customer")}</MenuItem>
                <MenuItem value="driver">{t("driver")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <FormControl fullWidth style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
              <InputLabel id="campaign_device_type" sx={{ "&.Mui-focused": { color: MAIN_COLOR } }}>
                {t("device_type")}
              </InputLabel>
              <Select
                labelId="campaign_device_type"
                id="campaign_device_type"
                value={data.devicetype}
                label={t("device_type")}
                onChange={(e) => setData({ ...data, devicetype: e.target.value })}
              >
                <MenuItem value="All">{t("all")}</MenuItem>
                <MenuItem value="ANDROID">{t("android")}</MenuItem>
                <MenuItem value="IOS">{t("ios")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography style={{ fontFamily: FONT_FAMILY, color: colors.HEADER, fontSize: 13 }}>
              {t("campaign_save_hint")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Button
              style={{
                borderRadius: "19px",
                backgroundColor: MAIN_COLOR,
                minHeight: 50,
                width: "50%",
              }}
              onClick={handleSubmit}
              variant="contained"
            >
              <Typography style={{ color: colors.WHITE, fontSize: 16, fontFamily: FONT_FAMILY }}>
                {t("submit")}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Card>
      <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
        {commonAlert.msg}
      </AlertDialog>
    </div>
  );
};

export default EditCampaign;
